import { mergeObjectStrict } from '$gbusiness/helpers/util';
import { getItemPrice, getTaxPercent } from '../redux/cart/utils';
import { deriveRawToDistributionItem } from './distributionItem';
import OrderInvoiceItemModel, {
  deriveRawToOrderInvoiceItem,
  initialOrderInvoiceItem,
  recalculateItemPrice,
} from './orderInvoiceItem';
import TaxModel from './tax';
import VendorModel, { deriveRawToVendor } from './vendor';
import WarehouseModel, { deriveRawToWarehouse } from './warehouse';

export default interface InvoiceItemModel extends OrderInvoiceItemModel {
  invoiceItemId: number;
  orderId?: number;
  orderDetailsId?: number;
  taxRule?: TaxModel;
  qtyOrdered?: number;
  qtySent?: number;
  qtyReceived?: number;
  totalSent?: number;
  totalReceived?: number;
  originalQty?: number;
  vendor?: VendorModel;
  categoryStr: string;
  serials?: Array<string>;
  requireSerial?: boolean;
  distributions?: Array<any>;
  itemDescription?: string;
  invoiceDistributions?: Array<any>;
  warehouse?: WarehouseModel;
  tariffObj?: any;
  deptStr: string;
}

export const deriveRawToInvoiceItem = (raw, store, isInvoice = false) => {
  return {
    ...deriveRawToOrderInvoiceItem(raw, isInvoice),
    orderId: raw.orderId,
    taxRule: raw.taxRule,
    invoiceItemId: raw.id,
    qtyOrdered: raw.qtyOrdered || raw.pricing.qty || 0,
    qtySent: raw.qtySent || 0,
    qty: raw.qty,
    qtyReceived: raw.qtyReceived || 0,
    originalQty: raw.qtySent || 0,
    totalSent: Number(raw.totalSent) || 0,
    totalReceived: Number(raw.totalReceived) || 0,
    serials: raw.serials || [],
    requireSerial: raw.requireSerial || false,
    itemDescription: raw.itemDescription || '',
    warehouse: deriveRawToWarehouse(raw.warehouse),
    categoryStr: raw.categoryStr || (raw.categories || []).map((d) => d.name).join(', '),
    deptStr: (raw.departments || []).map((d) => d.name).join(', '),
    ...(raw.vendor && { vendor: deriveRawToVendor(raw.vendor) }),
    distributions: (raw.distributions || []).map(deriveRawToDistributionItem),
    invoiceDistributions: deriveInvoiceDistribution(raw, store),
  };
};

export const initialInvoiceItem: InvoiceItemModel = {
  ...initialOrderInvoiceItem,
  invoiceItemId: 0,
  qtySent: 0,
  qtyOrdered: 0,
  qtyReceived: 0,
  originalQty: 0,
  totalSent: 0,
  totalReceived: 0,
  categoryStr: '',
  deptStr: '',
  tariffObj: {},
};

export const deriveItemToInvoiceItem = (item, store: any = null, keepSamePrice = false, isEdit = false) => {
  const newItem: any = mergeObjectStrict(initialInvoiceItem, item);
  const qtySent = newItem?.qtySent || newItem?.qty || 0;

  const taxRate = store ? getTaxPercent(item.taxRule, store) : 0;
  let unitPrice = store ? getItemPrice(item, store, item.invoiceItemId || item.id || 0) : 0;
  if (keepSamePrice) unitPrice = Number(newItem.unitPrice);

  return {
    ...newItem,
    rawCost: item.rawCost,
    wholesalePrice: item.wholesalePrice,
    retailPrice: item.retailPrice,
    taxRule: item.taxRule,
    serials: item.serials || [],
    qtySent,
    settings: item.settings || {},
    requireSerial: item?.requireSerial || false,
    disableDiscount: item.disableDiscount ? true : false,
    distributions: (item.distributions || []).map(deriveRawToDistributionItem),
    invoiceDistributions: deriveInvoiceDistribution(item, store, isEdit),
    ...(item.orderDetailsId && { orderDetailsId: item.orderDetailsId }),
    totalSent: qtySent,
    ...(store && {
      taxRate,
      unitPrice,
    }),
  };
};

export const deriveRawItemToInvoiceItem = (item, store, dcRate = 0) => {
  const taxRate = getTaxPercent(item.tax, store);
  const unitPrice = getItemPrice(item, store);
  const disableDiscount = !!item.settings?.disableDiscount;
  const discountRate = disableDiscount ? 0 : dcRate;
  const order = {
    ...item,
    id: undefined,
    invoiceItemId: 0,
    unitPrice,
    qty: 1,
    discountRate,
    taxRate,
  };

  return mergeObjectStrict(
    {
      ...initialInvoiceItem,
      rawCost: item.rawCost,
      wholesalePrice: item.wholesalePrice,
      retailPrice: item.retailPrice,
      taxRule: item.tax,
      disableDiscount,
      requireSerial: item.settings?.requireSerial || false,
      quantity: item.quantity,
      distributions: (item.distributions || []).map(deriveRawToDistributionItem),
      invoiceDistributions: deriveInvoiceDistribution(item, store),
    },
    {
      ...recalculateItemPrice({ order }),
      qtySent: order.qty,
      totalSent: order.qty,
      barcode: item.barcode || '',
      itemName: item.name,
      itemId: item.id,
    },
  );
};

export const deriveInvoiceDistribution = (item, store: any = null, isEdit = false) => {
  if (!item?.distributions?.length) return [];

  const firstWarehouseId = item?.distributions[0]?.warehouseId || 0;

  const defaultWarehouseId = item.warehouseId
    ? item.warehouseId
    : store
    ? store?.warehouse?.id || store?.warehouseId || firstWarehouseId
    : item?.store?.warehouseId || firstWarehouseId;

  let mainWarehouseId = 0;
  let highestQty = 0;

  const distributions = item.distributions.map((d) => {
    const invoiceDistribution = (item.invoiceDistributions || []).find(
      (i) => i.warehouseId === d.warehouseId,
    );
    if (invoiceDistribution) {
      if (invoiceDistribution.quantity > highestQty) {
        highestQty = invoiceDistribution.quantity;
        mainWarehouseId = d.warehouseId;
      }
      return {
        ...invoiceDistribution,
        maxQty: d.quantity + (isEdit ? invoiceDistribution.quantity : 0),
        orderOos: d.orderOos,
      };
    }

    const quantity =
      defaultWarehouseId === d.warehouseId ? (item.qtySent === undefined ? 1 : item.qtySent || 0) : 0;
    if (quantity > highestQty) {
      highestQty = quantity;
      mainWarehouseId = d.warehouseId;
    }

    return {
      id: 0,
      invoiceItemId: item?.invoiceItemId || 0,
      warehouse: d.warehouse,
      warehouseId: d.warehouseId,
      short: d.short,
      orderOos: d.orderOos,
      maxQty: d.quantity,
      quantity,
    };
  });
  return distributions.map((d) => ({
    ...d,
    isActive: highestQty ? d.warehouseId === mainWarehouseId : d.warehouseId === defaultWarehouseId,
  }));
};
